import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { saveStripeRestaurantDashboard, saveStripeProcessingFee } from '../../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Switch, InputAdornment
} from '@mui/material'
import CreditCardIcon from '@mui/icons-material/CreditCard'

const SAVE_RESTAURANT_DASHBOARD = gql`${saveStripeRestaurantDashboard}`
const SAVE_PROCESSING_FEE = gql`${saveStripeProcessingFee}`

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }

function Stripe(props) {
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Saves on flip: the master switch for the
  // Stripe section of restaurant dashboards. Each restaurant is also switched
  // on individually on the Stripe Restaurants page.
  const [dashboardOn, setDashboardOn] = useState(!!props.stripeRestaurantDashboard)
  const [saveDashboard, { loading: dashboardSaving }] = useMutation(SAVE_RESTAURANT_DASHBOARD, {
    onCompleted: (d) => setDashboardOn(!!d?.saveStripeRestaurantDashboard?.stripeRestaurantDashboard),
    onError: () => {
      setDashboardOn(!!props.stripeRestaurantDashboard)
      setError('Failed to change the restaurant dashboard switch.')
      setTimeout(() => setError(''), 3000)
    }
  })

  // Default card processing fee restaurants pay on Stripe orders, out of their
  // share. Individual restaurants can be given their own on Stripe Restaurants.
  const [feePercent, setFeePercent] = useState(props.stripeProcessingFeePercent != null ? String(props.stripeProcessingFeePercent) : '3.5')
  const [feeFixed, setFeeFixed] = useState(props.stripeProcessingFeeFixed != null ? Number(props.stripeProcessingFeeFixed).toFixed(2) : '0.30')
  const [saveFee, { loading: feeSaving }] = useMutation(SAVE_PROCESSING_FEE, {
    onCompleted: () => {
      setSuccess('Processing fee saved. New Stripe orders use it.')
      setError('')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (e) => {
      setError(e.message || 'Failed to save the processing fee.')
      setSuccess('')
      setTimeout(() => setError(''), 4000)
    }
  })
  const feeExample = (() => {
    const p = parseFloat(feePercent)
    const f = parseFloat(feeFixed)
    if (!Number.isFinite(p) || !Number.isFinite(f)) return null
    return (Math.round(((p / 100) * 50 + f) * 100) / 100).toFixed(2)
  })()

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #312e81 60%, #4338ca 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CreditCardIcon sx={{ color: '#a5b4fc', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Stripe</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>

        <Box sx={{ mb: 2, p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>Restaurant processing fee</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25, mb: 1.25 }}>
            Taken from the restaurant share on each Stripe order and kept by RushmyFood. The customer pays nothing extra.
            Applies to orders placed after saving. Override per restaurant on Stripe Restaurants.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small" type="number" label="Percent of card charge" value={feePercent}
              onChange={e => setFeePercent(e.target.value)}
              inputProps={{ min: 0, max: 20, step: 0.1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              sx={{ ...fieldSx, width: 170 }}
            />
            <Typography sx={{ color: '#6b7280', fontWeight: 700 }}>+</Typography>
            <TextField
              size="small" type="number" label="Fixed" value={feeFixed}
              onChange={e => setFeeFixed(e.target.value)}
              inputProps={{ min: 0, max: 5, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ ...fieldSx, width: 120 }}
            />
            <Button
              variant="outlined"
              disabled={feeSaving}
              onClick={() => saveFee({ variables: { percent: parseFloat(feePercent), fixed: parseFloat(feeFixed) } })}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#4338ca', color: '#4338ca' }}>
              {feeSaving ? 'Saving...' : 'Save fee'}
            </Button>
          </Box>
          {feeExample && (
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 1 }}>
              On a $50.00 card charge the restaurant pays ${feeExample}.
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2, p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>Stripe in restaurant dashboards</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25 }}>
              Balance, payouts, onboarding, transactions and refunds. Also turn it on per restaurant in Stripe Restaurants.
            </Typography>
          </Box>
          {dashboardSaving && <CircularProgress size={14} />}
          <Switch
            checked={dashboardOn}
            disabled={dashboardSaving}
            onChange={e => {
              setDashboardOn(e.target.checked)
              saveDashboard({ variables: { enabled: e.target.checked } })
            }}
            inputProps={{ 'aria-label': 'Stripe in restaurant dashboards' }}
          />
        </Box>

        {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}
      </Box>
    </Box>
  )
}

export default withTranslation()(Stripe)